import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../app/hooks";
import {
  fetchConsents,
  grantConsent,
  withdrawConsent,
} from "../../features/members/slice-extensions/members-slice-extension";
import { ConsentResponse } from "../../features/members/contracts/ConsentResponse";
import { ConsentPurpose } from "../../features/members/enums/ConsentPurpose";
import {
  CONSENT_PURPOSES,
  CURRENT_CONSENT_VERSIONS,
  GUARDIAN_REQUIRED_PURPOSES,
} from "../../features/members/consent-documents";
import { Member } from "../../features/members/models/Member";
import MemberEmailPreferencesRow from "./MemberEmailPreferencesRow";
import { ROUTES } from "../../routes";
import { formatMediumDate } from "../../features/utils/displayFormat";
import { getDisplayLocale } from "../../features/utils/timezoneUtils";

interface MemberConsentSectionProps {
  member: Member;
}

/** Public, shareable document a member reads before consenting, per purpose. */
const DOCUMENT_ROUTES: Partial<Record<ConsentPurpose, string>> = {
  [ConsentPurpose.TermsOfService]: ROUTES.MEMBER_TERMS_OF_SERVICE,
  [ConsentPurpose.PrivacyPolicy]: ROUTES.MEMBER_PRIVACY_POLICY,
};

/**
 * Consent management for a member: an instructor records and withdraws the member's consents
 * (Terms, Privacy, Marketing) and sees the full history. Consent is data, not a checkbox —
 * withdrawal is a state change, so withdrawn records stay visible.
 */
const MemberConsentSection: React.FC<MemberConsentSectionProps> = ({ member }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const purposeLabel = (purpose: ConsentPurpose): string =>
    t(`members.consents.purposes.${purpose}`, purpose);
  const [consents, setConsents] = useState<ConsentResponse[]>([]);
  const [busyPurpose, setBusyPurpose] = useState<ConsentPurpose | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const loadConsents = useCallback(async () => {
    const result = await dispatch(fetchConsents(member.id));
    if (fetchConsents.fulfilled.match(result)) {
      setConsents(result.payload);
    }
  }, [dispatch, member.id]);

  useEffect(() => {
    loadConsents();
  }, [loadConsents]);

  const latestForPurpose = (purpose: ConsentPurpose): ConsentResponse | undefined =>
    consents
      .filter((c) => c.purpose === purpose)
      .sort((a, b) => new Date(b.grantedAt).getTime() - new Date(a.grantedAt).getTime())[0];

  const formatDate = (value: string): string => formatMediumDate(value, getDisplayLocale());
  // The label prints the "v" itself; a version recorded as "v1.0" must not read "vv1.0".
  const versionNumber = (version: string): string => version.replace(/^v/i, "");

  const handleCopyLink = async (route: string) => {
    const url = `${window.location.origin}${route}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("members.consents.linkCopied", "Link copied — share it with the member."));
    } catch {
      toast.error(t("members.consents.linkCopyFailed", "Could not copy the link."));
    }
  };

  const handleRecord = async (purpose: ConsentPurpose) => {
    // A minor's terms/privacy consent must come from a guardian — stop early with a clear
    // message rather than letting the server reject it.
    const needsGuardian =
      member.isMinor && GUARDIAN_REQUIRED_PURPOSES.includes(purpose);
    if (needsGuardian && !member.guardianMemberId) {
      toast.error(t("members.consents.minorNeedsGuardian", "This member is a minor. Set a guardian on the member before recording this consent."));
      return;
    }

    setBusyPurpose(purpose);
    try {
      const result = await dispatch(
        grantConsent({
          memberId: member.id,
          request: {
            purpose,
            documentVersion: CURRENT_CONSENT_VERSIONS[purpose],
            grantedByMemberId: needsGuardian ? member.guardianMemberId : null,
          },
        })
      );
      if (grantConsent.fulfilled.match(result)) {
        toast.success(
          t("members.consents.recorded", "{{purpose}} consent recorded.", { purpose: purposeLabel(purpose) })
        );
        await loadConsents();
      } else {
        toast.error(result.payload?.message || t("members.consents.recordFailed", "Failed to record consent."));
      }
    } finally {
      setBusyPurpose(null);
    }
  };

  const handleWithdraw = async (purpose: ConsentPurpose, consentId: string) => {
    setBusyPurpose(purpose);
    try {
      const result = await dispatch(
        withdrawConsent({ memberId: member.id, consentId })
      );
      if (withdrawConsent.fulfilled.match(result)) {
        toast.success(
          t("members.consents.withdrawnToast", "{{purpose}} consent withdrawn.", { purpose: purposeLabel(purpose) })
        );
        await loadConsents();
      } else {
        toast.error(t("members.consents.withdrawFailed", "Failed to withdraw consent."));
      }
    } finally {
      setBusyPurpose(null);
    }
  };

  return (
    <div className="with-border rounded-2xl p-4 lg:p-6">
      <h3 className="text-grey-10 dark:text-grey-90 mb-4">
        {t("members.consents.title", "Consents & privacy")}
      </h3>

      <div className="space-y-3">
        {CONSENT_PURPOSES.map((purpose) => {
          const latest = latestForPurpose(purpose);
          const isActive = latest?.isActive ?? false;
          const isBusy = busyPurpose === purpose;

          return (
            <div
              key={purpose}
              className="flex items-center justify-between gap-3 flex-wrap"
            >
              <div className="min-w-0">
                <p className="text-grey-10 dark:text-white text-sm font-medium">
                  {purposeLabel(purpose)}
                </p>
                {latest ? (
                  <p className="text-grey-30 dark:text-grey-70 text-xs">
                    {isActive
                      ? t("members.consents.granted", "Granted {{date}} · v{{version}}", {
                          date: formatDate(latest.grantedAt),
                          version: versionNumber(latest.documentVersion),
                        })
                      : t("members.consents.withdrawnAt", "Withdrawn {{date}}", {
                          date: latest.withdrawnAt
                            ? formatDate(latest.withdrawnAt)
                            : "",
                        })}
                    {latest.grantedByMemberId
                      ? t("members.consents.byGuardian", " · by guardian")
                      : ""}
                  </p>
                ) : (
                  <p className="text-grey-30 dark:text-grey-70 text-xs">
                    {t("members.consents.notRecorded", "Not recorded")}
                  </p>
                )}
                {DOCUMENT_ROUTES[purpose] && (
                  <div className="flex items-center gap-3 mt-1">
                    <a
                      href={DOCUMENT_ROUTES[purpose]}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-40 dark:text-primary-90 hover:underline text-xs font-medium"
                    >
                      {t("members.consents.view", "View")}
                    </a>
                    <button
                      type="button"
                      onClick={() => handleCopyLink(DOCUMENT_ROUTES[purpose]!)}
                      className="text-primary-40 dark:text-primary-90 hover:underline text-xs font-medium"
                    >
                      {t("members.consents.copyLink", "Copy link")}
                    </button>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3">
                {isActive ? (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-xs font-medium">
                    {t("members.consents.active", "Active")}
                  </span>
                ) : null}
                {isActive && latest ? (
                  <button
                    type="button"
                    disabled={isBusy}
                    onClick={() => handleWithdraw(purpose, latest.id)}
                    className="text-red-600 dark:text-red-400 hover:underline text-sm font-medium disabled:opacity-50"
                  >
                    {t("members.consents.withdraw", "Withdraw")}
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={isBusy}
                    onClick={() => handleRecord(purpose)}
                    className="text-primary-40 dark:text-primary-90 hover:underline text-sm font-medium disabled:opacity-50"
                  >
                    {isBusy
                      ? t("members.consents.saving", "Saving...")
                      : t("members.consents.record", "Record consent")}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {consents.length > 0 && (
        <div className="mt-4 pt-4 border-t border-grey-70 dark:border-primary-20">
          <button
            type="button"
            onClick={() => setShowHistory((prev) => !prev)}
            className="text-primary-40 dark:text-primary-90 hover:underline text-sm font-medium"
          >
            {showHistory
              ? t("members.consents.hideHistory", "Hide history")
              : t("members.consents.history", "History ({{count}})", { count: consents.length })}
          </button>

          {showHistory && (
            <ul className="mt-3 space-y-2">
              {consents.map((consent) => (
                <li
                  key={consent.id}
                  className="text-grey-30 dark:text-grey-70 text-xs"
                >
                  {t("members.consents.historyEntry", "{{purpose}} · v{{version}} · granted {{date}}", {
                    purpose: purposeLabel(consent.purpose),
                    version: versionNumber(consent.documentVersion),
                    date: formatDate(consent.grantedAt),
                  })}
                  {consent.withdrawnAt
                    ? t("members.consents.historyWithdrawn", " · withdrawn {{date}}", {
                        date: formatDate(consent.withdrawnAt),
                      })
                    : t("members.consents.historyActive", " · active")}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <MemberEmailPreferencesRow memberId={member.id} />
    </div>
  );
};

export default MemberConsentSection;
